// Angular imports
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

// CC-Common imports
import { IconConstants } from 'cc-common';

// Third party imports
import notify from 'devextreme/ui/notify';
import { DxDataGridComponent } from 'devextreme-angular';

// constant imports
import { Constants } from '../../../../../_constants/constants';
import { MessageInterval } from '../../../../../_constants/messages-intervals';

// Service imports
import { ServerErrorService } from '../../../../../_services/shared-services/server-error.service';
import { ARAdjustmentService } from '../../../_services/account-receivable/ar-adjustment.service';
import { PrecisionService } from '../../../../../_services/shared-services/precision.service';

// Model imports
import { ARAdjustmentParameterDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-parameter-dto';
import { ARAdjustmentItemResultDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-item-result-dto';
import { ARAdjustmentWriteOffParameterDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-writeoff-parameter-dto';
import { ARAdjustmentWriteOffItemParametersDto } from '../../../_models/account-receivable/ar-adjustment/ar-adjustment-writeoff-item-parameters-dto';

@Component({
    selector: 'app-ar-adjustment-write-off-editor',
    templateUrl: './ar-adjustment-write-off-editor.component.html',
    styleUrls: ['./ar-adjustment-write-off-editor.component.scss']
})
export class ARAdjustmentWriteOffEditorComponent implements OnInit {
    @ViewChild('writeOffAdjustmentGrid', { static: false }) writeOffAdjustmentGrid: DxDataGridComponent;
    @Input() visible = false;
    @Input() arAdjustmentParam: ARAdjustmentParameterDto;
    @Input() arAdjustmentItems: ARAdjustmentItemResultDto[];
    @Output() onWriteOffEditorClose = new EventEmitter<boolean>();

    defaultLabelLocation: string = Constants.defaultFormLabelLocation;

    loadingVisible = false;
    closeIcon: string = IconConstants.CLOSE;
    saveIcon: string = IconConstants.CHECK_MARK;
    currencyFormat: any;
    writeOffAmount: number;
    writeOffParam: ARAdjustmentWriteOffParameterDto = new ARAdjustmentWriteOffParameterDto();

    constructor(
        private readonly errorService: ServerErrorService,
        private readonly arAdjustmentService: ARAdjustmentService,
        private readonly precisionService: PrecisionService
    ) {
        this.currencyFormat = this.precisionService.getCurrencyFormat();
    }

    ngOnInit() {
        if (!!this.arAdjustmentParam) {
            this.writeOffParam.adjustmentId = this.arAdjustmentParam.adjustmentId;
            this.writeOffParam.communityId = this.arAdjustmentParam.communityId;
            this.writeOffParam.managementCompanyId = this.arAdjustmentParam.managementCompanyId;
        }
        if (this.arAdjustmentItems.length === 1) {
            this.writeOffAmount = this.arAdjustmentItems[0].openBalance;
        } else {
            this.arAdjustmentItems.forEach((amount) => {
                amount.writeOffAmount = amount.openBalance;
            });
        }
        this.writeOffParam.writeOffDate = new Date();
    }

    onHidden(modified): void {
        this.visible = false;
        this.onWriteOffEditorClose.emit(modified);
    }

    applyAmountChanged(e, data: ARAdjustmentItemResultDto) {
        if (data.isSelect && e.value > data.openBalance) {
            notify(
                'Line item writeoff amount should not be greter than line item due amount.',
                'error',
                MessageInterval.errorMsgInterval
            );
            e.value = data.openBalance;
            data.isSelect = false;
            this.writeOffAdjustmentGrid.instance.refresh();
        } else {
            data.writeOffAmount = e.value;
        }
    }

    onWriteOffClick(): void {
        this.writeOffParam.items = [];
        let item: ARAdjustmentWriteOffItemParametersDto = new ARAdjustmentWriteOffItemParametersDto();
        if (this.arAdjustmentItems.length === 1) {
            if (this.writeOffAmount > this.arAdjustmentItems[0].openBalance) {
                notify(
                    'The Adjustment writeoff amount should not be greter than open balance.',
                    'error',
                    MessageInterval.errorMsgInterval
                );
            } else {
                item.adjustmentItemId = this.arAdjustmentItems[0].arAdjustmentItemId;
                item.writeOffAmount = this.writeOffAmount;
                this.writeOffParam.items.push(item);
                this.writeOffAdjustment(this.writeOffParam);
            }
        } else {
            const checkIsSelected = this.arAdjustmentItems.some((s) => s.isSelect === true);
            if (checkIsSelected) {
                this.arAdjustmentItems.forEach((e) => {
                    item = new ARAdjustmentWriteOffItemParametersDto();
                    if (e.isSelect) {
                        item.adjustmentItemId = e.arAdjustmentItemId;
                        item.writeOffAmount = e.writeOffAmount;
                        this.writeOffParam.items.push(item);
                    }
                });
                this.writeOffAdjustment(this.writeOffParam);
            } else {
                notify(
                    'Please select atleast one line item to write-off adjustment',
                    'error',
                    MessageInterval.errorMsgInterval
                );
            }
        }
    }

    private writeOffAdjustment(param: ARAdjustmentWriteOffParameterDto): void {
        this.loadingVisible = true;
        this.arAdjustmentService.writeOffARAdjustment(param).subscribe(
            () => {
                this.onHidden(true);
                notify(
                    'The Adjustment writeoff has been successfully posted.',
                    'success',
                    MessageInterval.successMsgInterval
                );
                this.loadingVisible = false;
            },
            (error) => {
                this.loadingVisible = false;
                this.errorService.displayError(error);
                this.onHidden(false);
            }
        );
    }
}
